import { useEffect, useRef, useState } from 'react';

/** A drag carries files (not text/elements) when its dataTransfer advertises the "Files" type. */
const dragHasFiles = (event: DragEvent): boolean =>
	!!event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files');

/**
 * Reports whether the user is currently dragging files anywhere over the window, so a drop
 * target can reveal itself. Uses the enter/leave counter pattern: dragenter and dragleave fire
 * for every element the cursor crosses, so a single boolean flickers — counting keeps it stable
 * until the drag truly leaves the window. dragover/drop are prevented (only while files are
 * present) so the browser doesn't navigate to the dropped file.
 */
export function useWindowFileDrag(): boolean {
	const [isDragging, setIsDragging] = useState(false);
	const enterCount = useRef(0);

	useEffect(() => {
		const onEnter = (event: DragEvent) => {
			if (!dragHasFiles(event)) return;
			enterCount.current += 1;
			setIsDragging(true);
		};
		const onLeave = (event: DragEvent) => {
			if (!dragHasFiles(event)) return;
			enterCount.current -= 1;
			if (enterCount.current <= 0) {
				enterCount.current = 0;
				setIsDragging(false);
			}
		};
		const onOver = (event: DragEvent) => {
			if (dragHasFiles(event)) event.preventDefault();
		};
		const onDrop = (event: DragEvent) => {
			if (dragHasFiles(event)) event.preventDefault();
			enterCount.current = 0;
			setIsDragging(false);
		};

		window.addEventListener('dragenter', onEnter);
		window.addEventListener('dragleave', onLeave);
		window.addEventListener('dragover', onOver);
		window.addEventListener('drop', onDrop);
		return () => {
			window.removeEventListener('dragenter', onEnter);
			window.removeEventListener('dragleave', onLeave);
			window.removeEventListener('dragover', onOver);
			window.removeEventListener('drop', onDrop);
		};
	}, []);

	return isDragging;
}
